import { SearchActivityEntry } from '@web/shared/components/search/interfaces/search-activity-entry.interface';
import { parseISO } from 'date-fns';
import { Hits, RecordAny } from 'meilisearch';
import { SearchBidEntry } from './interfaces/search-bid-entry.interface';
import { SearchConstructionSiteEntry } from './interfaces/search-construction-site-entry.interface';
import { SearchContactEntry } from './interfaces/search-contact-entry.interface';
import { SearchDocumentAssetEntry } from './interfaces/search-document-asset-interface';
import { SearchEmployeeEntry } from './interfaces/search-employee-entry.interface';
import { SearchLeadEntry } from './interfaces/search-lead-entry.interface';
import { SearchNoteEntry } from './interfaces/search-notes-entry.interface';
import { SearchProjectEntry } from './interfaces/search-project-entry.interface';
import { SearchRealEstateEntry } from './interfaces/search-real-estate-entry.interface';
import { SearchScoutingEntry } from './interfaces/search-scouting-entry.interface';
import { SearchSupplierEntry } from './interfaces/search-supplier-entry.interface';
import { SearchTaskEntry } from './interfaces/search-task-interface';
import { SearchEntry } from './types/search-entry.type';

const mapContact = (data: RecordAny): SearchContactEntry => ({
  id: data['id'],
  name: data['_formatted']['name'],
  address: data['_formatted']['address'],
  code: data['code'],
  email: data['_formatted']['email'],
  kind: data['kind'],
  phoneNumber: data['_formatted']['phoneNumber'],
  searchEntryKind: 'contacts',
});

const mapLead = (data: RecordAny): SearchLeadEntry => ({
  id: data['id'],
  code: data['code'],
  profileName: data['_formatted']['profileName'],
  interestName: data['_formatted']['interestName'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  targetName: data['_formatted']['targetName'],
  searchEntryKind: 'leads',
});

const mapScouting = (data: RecordAny): SearchScoutingEntry => ({
  id: data['id'],
  code: data['code'],
  name: data['_formatted']['name'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  targetName: data['_formatted']['targetName'],
  referralName: data['_formatted']['referralName'],
  searchEntryKind: 'scoutings',
});

const mapBid = (data: RecordAny): SearchBidEntry => ({
  id: data['id'],
  code: data['code'],
  name: data['_formatted']['name'],
  profileName: data['_formatted']['profileName'],
  interestName: data['_formatted']['interestName'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  targetName: data['_formatted']['targetName'],
  searchEntryKind: 'bids',
});

const mapProject = (data: RecordAny): SearchProjectEntry => ({
  id: data['id'],
  code: data['code'],
  name: data['_formatted']['name'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  searchEntryKind: 'projects',
});

const mapConstructionSite = (data: RecordAny): SearchConstructionSiteEntry => ({
  id: data['id'],
  code: data['code'],
  name: data['_formatted']['name'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  searchEntryKind: 'construction-sites',
});

const mapEmployee = (data: RecordAny): SearchEmployeeEntry => ({
  id: data['id'],
  name: data['_formatted']['name'],
  address: data['_formatted']['address'],
  city: data['_formatted']['city'],
  state: data['_formatted']['state'],
  country: data['_formatted']['country'],
  zipCode: data['_formatted']['zipCode'],
  streetNumber: data['_formatted']['streetNumber'],
  code: data['code'],
  email: data['_formatted']['email'],
  phoneNumber: data['_formatted']['phoneNumber'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  searchEntryKind: 'employees',
  relatedTo: {
    id: data['relatedTo']['id'],
    code: data['relatedTo']['code'],
    aggregate: data['relatedTo']['aggregate'],
  },
});

const mapSupplier = (data: RecordAny): SearchSupplierEntry => ({
  id: data['id'],
  businessName: data['_formatted']['businessName'],
  address: data['_formatted']['address'],
  city: data['_formatted']['city'],
  state: data['_formatted']['state'],
  country: data['_formatted']['country'],
  zipCode: data['_formatted']['zipCode'],
  streetNumber: data['_formatted']['streetNumber'],
  code: data['code'],
  kind: data['kind'],
  email: data['_formatted']['email'],
  phoneNumber: data['_formatted']['phoneNumber'],
  vatCode: data['_formatted']['vatCode'],
  statusName: data['statusName'] ?? '',
  statusColor: data['statusColor'] ?? '',
  statusTextColor: data['statusTextColor'] ?? '',
  geo: {
    lat: data['_geo']['lat'],
    lng: data['_geo']['lng'],
  },
  searchEntryKind: 'suppliers',
});

const mapNote = (data: RecordAny): SearchNoteEntry => ({
  id: data['id'],
  title: data['_formatted']['title'],
  body: data['_formatted']['body'],
  attachments: data['_formatted']['attachments'],
  createdAt: parseISO(data['createdAt']),
  visibility: data['visibility'] === 'private' ? 'private' : 'shared',
  relatedTo: data['relatedTo']
    ? {
        id: data['relatedTo']['id'],
        code: data['relatedTo']['code'],
        kind: data['relatedTo']['kind'],
        name: data['relatedTo']['name'],
        aggregate: data['relatedTo']['aggregate'],
      }
    : null,
  searchEntryKind: 'notes',
});

const mapActivity = (data: RecordAny): SearchActivityEntry => ({
  id: data['id'],
  name: data['_formatted']['name'],
  description: data['_formatted']['description'],
  scheduledAt: parseISO(data['scheduledAt']),
  status: data['status'],
  createdAt: parseISO(data['createdAt']),
  relatedTo: {
    id: data['relatedTo']['id'],
    code: data['relatedTo']['code'],
    kind: data['relatedTo']['kind'],
    aggregate: data['relatedTo']['aggregate'],
  },
  searchEntryKind: 'activities',
});

const mapTask = (data: RecordAny): SearchTaskEntry => ({
  id: data['id'],
  name: data['_formatted']['name'],
  description: data['_formatted']['description'],
  status: data['status'],
  dueDate: data['dueDate'] ? parseISO(data['dueDate']) : null,
  priority: data['priority'],
  progress: data['progress'],
  assigneeUser: data['assigneeUser'],
  relatedTo: {
    id: data['relatedTo']['id'],
    code: data['relatedTo']['code'],
    kind: data['relatedTo']['kind'],
    aggregate: data['relatedTo']['aggregate'],
  },
  searchEntryKind: 'tasks',
});

const mapDocumentAssets = (data: RecordAny): SearchDocumentAssetEntry => ({
  id: data['id'],
  description: data['_formatted']['description'],
  documentTypeName: data['_formatted']['documentTypeName'],
  expirationDate: data['expirationDate'] ? parseISO(data['expirationDate']) : null,
  notes: data['notes'],
  protocol: data['protocol'],
  status: data['status'],
  versionNumber: data['versionNumber'],
  relatedTo: {
    id: data['relatedTo']['id'],
    code: data['relatedTo']['code'],
    kind: data['relatedTo']['kind'],
    aggregate: data['relatedTo']['aggregate'],
  },
  searchEntryKind: 'document-assets',
});

const mapRealEstate = (data: RecordAny): SearchRealEstateEntry => ({
  id: data['id'],
  code: data['code'],
  kind: data['kind'],
  name: data['_formatted']['name'],
  address: data['_formatted']['address'],
  city: data['_formatted']['city'],
  state: data['_formatted']['state'],
  country: data['_formatted']['country'],
  zipCode: data['_formatted']['zipCode'],
  streetNumber: data['_formatted']['streetNumber'],
  typology: data['_formatted']['typology'],
  searchEntryKind: 'real-estates',
  geo: {
    lat: data['_geo']['lat'],
    lng: data['_geo']['lng'],
  },
});

const mappers: Record<SearchEntry['searchEntryKind'], (data: RecordAny) => SearchEntry> = {
  contacts: mapContact,
  leads: mapLead,
  scoutings: mapScouting,
  bids: mapBid,
  notes: mapNote,
  activities: mapActivity,
  'real-estates': mapRealEstate,
  projects: mapProject,
  'construction-sites': mapConstructionSite,
  tasks: mapTask,
  'document-assets': mapDocumentAssets,
  employees: mapEmployee,
  suppliers: mapSupplier,
};

const cleanResult = (data: SearchEntry | undefined): data is SearchEntry => Boolean(data);

export const toSearchEntries = (data: Hits<RecordAny>): SearchEntry[] => {
  return data
    .map((res) => {
      const kind: SearchEntry['searchEntryKind'] = res._federation?.indexUid as SearchEntry['searchEntryKind'];
      if (!kind) return;
      if (!res._formatted) return;
      return mappers[kind](res);
    })
    .filter(cleanResult);
};
